import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, Loader2, Fingerprint, Users, ScanFace, Building2, KeyRound, UserSquare2, ShieldAlert,
} from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { useI18n } from "i18n";
import api from "lib/api";

// Icon + accent per result origin, so the eye finds the right group before reading.
const KIND = {
  biometric:  { icon: Fingerprint,  cls: "text-primary" },
  account:    { icon: Users,        cls: "text-sky-500" },
  session:    { icon: ScanFace,     cls: "text-emerald-500" },
  company:    { icon: Building2,    cls: "text-amber-500" },
  client_app: { icon: KeyRound,     cls: "text-violet-500" },
  person:     { icon: UserSquare2,  cls: "text-cyan-500" },
  alert:      { icon: ShieldAlert,  cls: "text-red-500" },
};

export default function SearchResults() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState(q);

  const run = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setData(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/api/search/?q=${encodeURIComponent(query.trim())}`);
      setData(r.data);
    } catch {
      setData({ query, count: 0, groups: [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { setTerm(q); run(q); }, [q, run]);

  const label = (g) => t(`search.type.${g.type}`, g.label);

  return (
    <div className="animate-fade-in w-full">
      <div className="flex items-center gap-2 mb-1">
        <Search className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">{t("search.title", "Busca")}</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        {data
          ? t("search.found", "{n} resultado(s) para “{q}”")
              .replace("{n}", data.count).replace("{q}", data.query)
          : t("search.hint", "Nome, e-mail, CPF, telefone, empresa, app ou id de sessão.")}
      </p>

      <form className="relative mb-6 max-w-2xl"
        onSubmit={(e) => { e.preventDefault(); setParams({ q: term.trim() }); }}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input value={term} onChange={(e) => setTerm(e.target.value)} autoFocus
          placeholder={t("search.placeholder", "Buscar em todo o sistema…")}
          className="w-full h-11 pl-9 pr-3 rounded-lg border border-border bg-background text-sm" />
      </form>

      {loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {!loading && data && data.count === 0 && (
        <div className="text-center py-16 text-sm text-muted-foreground">
          <Search className="h-10 w-10 mx-auto mb-3 opacity-40" />
          {t("search.empty", "Nada encontrado. Valores sensíveis (e-mail, CPF, telefone) são cifrados e só casam pelo valor exato.")}
        </div>
      )}

      {!loading && data?.groups?.map((g) => {
        const meta = KIND[g.type] || { icon: Search, cls: "text-muted-foreground" };
        const Icon = meta.icon;
        return (
          <Card key={g.type} className="mb-4">
            <CardContent className="pt-5">
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`h-4 w-4 ${meta.cls}`} />
                <h2 className="text-sm font-semibold tracking-tight">{label(g)}</h2>
                <span className="text-xs text-muted-foreground">({g.hits.length})</span>
              </div>
              <div className="divide-y divide-border">
                {g.hits.map((h) => (
                  <button key={`${h.type}-${h.id}`} type="button"
                    onClick={() => h.url && navigate(h.url)}
                    className="w-full text-left py-2.5 px-2 -mx-2 rounded-lg hover:bg-muted/40 transition-colors flex items-center gap-3">
                    <Icon className={`h-4 w-4 shrink-0 ${meta.cls} opacity-70`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate">{h.title}</span>
                      {h.subtitle && (
                        <span className="block text-xs text-muted-foreground truncate">{h.subtitle}</span>
                      )}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                      {String(h.id).slice(0, 8)}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
